import React, { useState, useMemo, useEffect, useCallback } from "react";
import { appearancesToResults, filterResults } from "../../core/data/results";
import { buildCompetitorMap } from "../../core/data/competitors";
import { filterAppearancesByType, advertiserOptions } from "../../core/data/marketOverview";
import { listingsForLinking } from "../../core/data/reportCore";
import { yourBusinessKey } from "../../core/data/advertiserOverview";
import { parseReportAddress, buildReportAddress, onlyKnown } from "../../core/data/reportAddress";
import { REPORT } from "../../core/theme/reportTheme";
import { visiblePages, pageShowsPin } from "./pageRegistry";
import useReportData from "./useReportData";
import useMarketOverview from "./useMarketOverview";
import useAdvertiserOverview from "./useAdvertiserOverview";
import ReportHeader from "./components/ReportHeader";
import PinChip from "./components/PinChip";
import Modal from "./components/Modal";
import PickerPanel from "./pages/advertiser/components/PickerPanel";
import LinkListingsPanel from "./pages/advertiser/components/LinkListingsPanel";

// The report: the shared header (job, sampling, filters, page tabs) and the
// chosen page. Tab, filters and the pinned advertiser live in the page
// address (?job=..&tab=..&device=..&kw=..&pc=..&type=..&adv=..&pin=..), so they
// apply on every tab, survive a refresh and can be copied. Opening a
// different job starts with no filters. If the job has a "your business"
// website and nothing is pinned, it is pinned straight away.
export default function ReportScreen({ job }) {
  const [view, setView] = useState(() => startingView(job));
  const [panel, setPanel] = useState(null); // "picker" | "links" | null
  const update = useCallback(changes => setView(current => ({ ...current, ...changes })), []);

  // Keep the address in step with the view (replace, so Back leaves the report).
  useEffect(() => {
    if (!job) return;
    const address = buildReportAddress({ jobId: job.id, ...view });
    window.history.replaceState(null, "", `${window.location.pathname}${address}`);
  }, [job, view]);

  const { data, loading, setBusinessType, setListingLink } = useReportData(job?.id);

  // The Advertisers filter's list: everyone seen in the job. Ticked keys the
  // job doesn't have (an out-of-date link, a listing since linked) are ignored.
  const advertiserList = useMemo(() => (data ? advertiserOptions(data) : []), [data]);
  const advertisers = data ? onlyKnown(view.advertisers, advertiserList.map(a => a.key)) : [];
  const advertisersKey = advertisers.join("\n");
  // Highlighted in yellow on every table (not a filter).
  const highlights = data ? onlyKnown(view.highlights, advertiserList.map(a => a.key)) : [];

  const filters = {
    device: view.device,
    keywords: view.keywords,
    postcodes: view.postcodes,
    businessTypes: view.businessTypes,
    advertisers,
  };
  const overview = useMarketOverview(data, filters);
  const advertiser = useAdvertiserOverview(data, view.pin, filters);

  // The other pages (PPC ...) use the same filters, including Business type and Advertisers.
  const typeFiltered = useMemo(
    () => (data ? appearancesToResults(filterAppearancesByType(data, view.businessTypes, advertisersKey ? advertisersKey.split("\n") : [])) : []),
    [data, view.businessTypes, advertisersKey]
  );
  const results = useMemo(
    () => filterResults(typeFiltered, view.keywords, view.postcodes, view.device),
    [typeFiltered, view.keywords, view.postcodes, view.device]
  );
  const competitors = useMemo(() => buildCompetitorMap(results), [results]);
  const listings = useMemo(() => (data ? listingsForLinking(data) : []), [data]);

  const closePanel = useCallback(() => setPanel(null), []);

  if (!job) return null;
  if (loading || !overview || !advertiser) return <div className={REPORT.loading}>Loading snapshot…</div>;

  const pinned = advertiser.pinned;
  const context = {
    overview,
    advertiser,
    onSetType: setBusinessType,
    results,
    competitors,
    pinKey: pinned ? pinned.key : null,
    highlightKeys: highlights,
    onPin: key => { update({ pin: key }); setPanel(null); },
    onShowTab: tab => update({ tab }),
    onOpenAdvertiser: key => update({ tab: "advertiser", pin: key }),
    onOpenLinkPanel: () => setPanel("links"),
  };
  const { pages, tabs } = visiblePages(context);
  const active = pages.find(page => page.id === view.tab) || pages[0];
  const ActivePage = active.component;

  // Websites a listing can be linked to: the pinned one first.
  const websites = [...new Set([
    ...(pinned && pinned.domain ? [pinned.domain] : []),
    ...advertiser.picker.map(p => p.key).filter(k => !k.startsWith("name:")).sort(),
  ])];

  const tabRight = pageShowsPin(active) ? (
    <PinChip
      pinned={pinned}
      onChange={() => setPanel("picker")}
      onClear={() => update({ pin: null })}
      showTypeNote={active.id === "advertiser" && Boolean(pinned && (pinned.excludedByTypeFilter || pinned.excludedByAdvertiserFilter))}
    />
  ) : null;

  return (
    <div className={REPORT.screen}>
      <ReportHeader
        overview={overview}
        job={job}
        filters={{ device: view.device, keywords: view.keywords, postcodes: view.postcodes, businessTypes: view.businessTypes, advertisers, highlights }}
        onFilters={f => update(f)}
        advertiserOptions={advertiserList}
        tabs={tabs}
        activeId={active.id}
        onTab={tab => update({ tab })}
        tabRight={tabRight}
      />

      <div className={REPORT.pageArea}>
        {active.fullHeight ? (
          <ActivePage {...context} />
        ) : (
          <div className={REPORT.pageScroll}><ActivePage {...context} /></div>
        )}
      </div>

      {panel === "picker" && (
        <Modal onClose={closePanel}>
          <PickerPanel
            result={advertiser}
            onPin={context.onPin}
            highlightKeys={highlights}
            cornerButton={<button type="button" onClick={closePanel} className="w-6 h-6 rounded-md text-sm text-slate-500 hover:text-slate-900 hover:bg-violet-100" aria-label="Close advertiser picker">✕</button>}
          />
        </Modal>
      )}
      {panel === "links" && (
        <Modal onClose={closePanel} wide>
          <LinkListingsPanel listings={listings} websites={websites} onSetListingLink={setListingLink} onClose={closePanel} />
        </Modal>
      )}
    </div>
  );
}

// The view a report opens with: from the address if it's for this job,
// otherwise no filters. Either way, your business is pinned if nothing is.
function startingView(job) {
  const address = parseReportAddress(typeof window !== "undefined" ? window.location.search : "");
  const own = yourBusinessKey(job);
  if (!job || address.jobId !== job.id) {
    return { tab: "overview", device: "all", keywords: [], postcodes: [], businessTypes: [], advertisers: [], highlights: [], pin: own };
  }
  return {
    tab: address.tab || "overview",
    device: (job.devices || []).includes(address.device) ? address.device : "all",
    keywords: onlyKnown(address.keywords, job.keywords),
    postcodes: onlyKnown(address.postcodes, job.locations),
    businessTypes: address.businessTypes,
    advertisers: address.advertisers,
    highlights: address.highlights,
    pin: address.pin || own,
  };
}
